type ExcludeVariants = -1 | -2 | -3;
type ExcludeFn = (data: {
  isEmbeddedWebView: boolean;
}) => Promise<void | ExcludeVariants> | (void | ExcludeVariants);

type ExperimentDefinition<T extends number, V extends readonly number[]> = {
  name: string;
  shortName: `exp_${T}`;
  canonical: T;
  variants: V;
  exclude?: ExcludeFn;
};

type ExperimentDefinitionMap<T extends number, V extends readonly number[]> = {
  [K in T]: ExperimentDefinition<K, V>;
};

function defineExperiments<T extends number, V extends readonly number[]>(
  defs: ExperimentDefinitionMap<T, V>
) {
  return defs;
}

/**
 *
 * "ExperimentDefinitions" contains definitions for all the
 * experiments that are currently in the web app.
 *
 * In order to use "useExperiment", and experiment MUST be defined here first.
 *
 * This is where you should define "exclusion" criteria for pre-signup experiments.
 * To do this, define an "exclude" function on the experiment definition. "exclude"
 * can return undefined | Promise<undefined> | ExperimentVariant | ExcludeVariants.
 *
 * If undefined or Promise<undefined> is returned, the regular enrollment algo
 * will run. Otherwise, the value returned from this function will be used for the user.
 *
 * "exclude" takes 1 argument. It is typed as:
 *
 * {
 * isEmbeddedWebView: boolean;
 *  }
 *
 * isEmbeddedWebView will be true if the user is using one of our mobile apps.
 *
 */

export const ExperimentDefinitions = defineExperiments({
  164: {
    name: 'Exp 164', // FlexCredits Default for New Users - will be part of Single Price Per Credit enrollment
    shortName: 'exp_164',
    canonical: 164,
    variants: [0, 1],
  },

  165: {
    name: 'Exp 165', // Single Price Per Credit - in planning
    shortName: 'exp_165',
    canonical: 165,
    variants: [0, 1],
  },

  170: {
    name: 'Exp 170', // Onboarding x FBS - waiting on 193 result, but saw adherence
    shortName: 'exp_170',
    canonical: 170,
    variants: [0, 1],
  },

  171: {
    name: 'Exp 171', // MHR Redesign - waiting on Discovery Home to launch + mature
    shortName: 'exp_171',
    canonical: 171,
    variants: [0, 1],
  },

  173: {
    name: 'Exp 173', // PAYG - waiting for discussion with CPO
    shortName: 'exp_173',
    canonical: 173,
    variants: [0, 1],
  },

  178: {
    name: 'Exp 178', // Bi-weekly Cadence (post-checkout) - still data maturing
    shortName: 'exp_178',
    canonical: 178,
    variants: [0, 1],
  },

  181: {
    name: 'Exp 181', // FTC cancel flow - waiting for 186 to run
    shortName: 'exp_181',
    canonical: 181,
    variants: [0, 1],
  },

  183: {
    name: 'Exp 183', // Pre-checkout bi-weekly cadence - still data maturing
    shortName: 'exp_183',
    canonical: 183,
    variants: [0, 1],
  },

  186: {
    name: 'Exp 186', // FTC round 3 - being removed (CA customers have variant 2 experience)
    shortName: 'exp_186',
    canonical: 186,
    variants: [0, 1, 2],
  },

  187: {
    name: 'Exp 187', // FTC cancel 6-hour flow - waiting to be launched
    shortName: 'exp_187',
    canonical: 187,
    variants: [0, 1],
  },

  188: {
    name: 'Exp 188', // Discovery Home
    shortName: 'exp_188',
    canonical: 188,
    variants: [0, 1],
  },

  189: {
    name: 'Exp 189', // First box focus
    shortName: 'exp_189',
    canonical: 189,
    variants: [0, 1, 2],
  },

  193: {
    name: 'Exp 193', // Recsys Preference Collection
    shortName: 'exp_193',
    canonical: 193,
    variants: [0, 1],
  },

  192: {
    name: 'Exp 192', // Scannable Entries
    shortName: 'exp_192',
    canonical: 192,
    variants: [0, 1],
  },

  195: {
    name: 'Exp 195', // Destination Food Profile
    shortName: 'exp_195',
    canonical: 195,
    variants: [0, 1],
  },

  197: {
    name: 'Exp 197', // Tests removing Quiz Intro screen
    shortName: 'exp_197',
    canonical: 197,
    variants: [0, 1],
  },

  198: {
    name: 'Exp 198', // Order level incentives
    shortName: 'exp_198',
    canonical: 198,
    variants: [0, 1, 2],
  },

  199: {
    name: 'Exp 199', // Redesigned pre-auth catalog
    shortName: 'exp_199',
    canonical: 199,
    variants: [0, 1],
    exclude: (data) => {
      if (data.isEmbeddedWebView) {
        return -1;
      }
      return undefined;
    },
  },

  200: {
    name: 'Exp 200', // Plan Screen Flexibility (AKA ready-to-eat + heat-and-eat options)
    shortName: 'exp_200',
    canonical: 200,
    variants: [0, 1],
  },

  201: {
    name: 'Exp 201', // Health Goals in Funnel (different from Goals screen)
    shortName: 'exp_201',
    canonical: 201,
    variants: [0, 1],
  },

  203: {
    name: 'Exp 203', // Item-level discounts
    shortName: 'exp_203',
    canonical: 203,
    variants: [0, 1],
  },

  204: {
    name: 'Exp 204', // De-anchoring Cart for Exp 188 Users.
    shortName: 'exp_204',
    canonical: 204,
    variants: [0, 1],
  },
  205: {
    name: 'Exp 205', // GLP-1 in funnel
    shortName: 'exp_205',
    canonical: 205,
    variants: [0, 1],
  },
  206: {
    name: 'Exp 206', // Post-checkout Filters updates
    shortName: 'exp_206',
    canonical: 206,
    variants: [0, 1],
  },
  207: {
    name: 'Exp 207', // Post-Checkout RecSys Preference Collection
    shortName: 'exp_207',
    canonical: 207,
    variants: [0, 1],
  },
} as const satisfies ExperimentDefinitionMap<number, number[]>);

export type Experiments = keyof typeof ExperimentDefinitions;
export type DefinedExperiments = (typeof ExperimentDefinitions)[Experiments];
export type ExperimentVariants<D extends Experiments> =
  | (typeof ExperimentDefinitions)[D]['variants'][number]
  | ExcludeVariants;
